<template>
  <div class="file-uploader">
    <!-- Already uploaded state: show filename + "Cambiar" button -->
    <div v-if="hasFile && !selectedFile && !showInput" class="uploaded-file d-flex align-center">
      <v-tooltip bottom>
        <template v-slot:activator="{ on, attrs }">
          <v-chip color="success" text-color="white" small v-bind="attrs" v-on="on">
            <v-icon small left>mdi-check-circle</v-icon>
            {{ displayFilename }}
          </v-chip>
        </template>
        <span>{{ fullFilename }}</span>
      </v-tooltip>
      <v-btn text small color="primary" @click="showInput = true" :disabled="disabled" class="ml-2">
        <v-icon small left>mdi-pencil</v-icon>
        Cambiar
      </v-btn>
    </div>

    <!-- File selection area (drop zone + file input) -->
    <div v-else
         class="drop-zone"
         :class="{ 'drag-over': isDragging }"
         @drop.prevent="onDrop"
         @dragover.prevent="isDragging = true"
         @dragleave.prevent="isDragging = false">
      <v-file-input
        v-model="selectedFile"
        :label="label || 'Seleccionar archivo'"
        :accept="accept"
        :disabled="disabled || isUploading"
        outlined
        dense
        prepend-icon="mdi-paperclip"
        show-size
        :error="!!uploadError"
        :error-messages="uploadError ? [uploadError] : []"
      />
    </div>

    <!-- Upload button (appears after file selected) -->
    <div v-if="selectedFile" class="upload-actions mt-2 d-flex align-center">
      <v-btn
        color="primary"
        :loading="isUploading"
        :disabled="!selectedFile || isUploading"
        @click="doUpload"
        small
      >
        <v-icon left small>mdi-cloud-upload</v-icon>
        Subir
      </v-btn>
      <v-btn v-if="hasFile" text small @click="cancelChange" class="ml-2">
        Cancelar
      </v-btn>
    </div>
  </div>
</template>

<script>
export default {
  name: 'FileUploader',

  // Inject uploadFile function from DynamicComponentRenderer
  inject: {
    uploadFile: { default: null }
  },

  props: {
    // Accepts either string (legacy) or object {front_path, filename}
    value: {
      type: [String, Object],
      default: ''
    },
    label: {
      type: String,
      default: 'Seleccionar archivo'
    },
    accept: {
      type: String,
      default: '.pdf,.jpg,.jpeg,.png,.gif,.bmp,.tiff,.tif,.doc,.docx,.odt,.xls,.xlsx,.xlsm,.ods,.csv,.txt,.xml,.zip,.rar,.7z'
    },
    disabled: {
      type: Boolean,
      default: false
    }
  },

  data() {
    return {
      selectedFile: null,
      isUploading: false,
      isDragging: false,
      showInput: false,
      uploadError: null,
      uploadedFilename: null  // Store original filename after upload
    }
  },

  computed: {
    // Check if we have a valid uploaded file
    hasFile() {
      if (!this.value) return false
      if (typeof this.value === 'string') return this.value.length > 0
      return this.value.front_path && this.value.front_path.length > 0
    },

    // Get the s3 path
    filePath() {
      if (!this.value) return ''
      if (typeof this.value === 'string') return this.value
      return this.value.front_path || ''
    },

    // Full filename for tooltip (no truncation)
    fullFilename() {
      if (this.value && typeof this.value === 'object' && this.value.filename) {
        return this.value.filename
      }
      if (this.uploadedFilename) {
        return this.uploadedFilename
      }
      const path = this.filePath
      if (!path) return ''
      const parts = path.split('/')
      return parts[parts.length - 1]
    },

    // Truncated filename for display
    displayFilename() {
      const filename = this.fullFilename
      if (!filename) return ''
      if (filename.length > 30) {
        return filename.substring(0, 27) + '...'
      }
      return filename
    }
  },

  methods: {
    onDrop(event) {
      this.isDragging = false
      const files = event.dataTransfer.files
      if (files && files.length > 0) {
        this.selectedFile = files[0]
      }
    },

    cancelChange() {
      this.selectedFile = null
      this.showInput = false
      this.uploadError = null
      // Keep uploadedFilename - user cancelled, so we still show the old file
    },

    clear() {
      this.selectedFile = null
      this.showInput = false
      this.uploadError = null
      this.uploadedFilename = null
      this.$emit('input', { front_path: '', filename: '' })
    },

    async doUpload() {
      if (!this.selectedFile) return

      if (!this.uploadFile) {
        this.uploadError = 'Upload function not available'
        console.error('uploadFile not injected - FileUploader must be used inside DynamicComponentRenderer')
        return
      }

      this.isUploading = true
      this.uploadError = null

      try {
        // Store original filename before upload
        const originalFilename = this.selectedFile.name

        // Call injected uploadFile function (from DynamicComponentRenderer)
        const s3Key = await this.uploadFile(this.selectedFile)

        // Store the original filename for display (local state backup)
        this.uploadedFilename = originalFilename

        // Emit object with front_path and filename
        this.$emit('input', {
          front_path: s3Key,
          filename: originalFilename
        })

        // Reset state
        this.selectedFile = null
        this.showInput = false
      } catch (error) {
        console.error('Upload failed:', error)
        this.uploadError = error.message || 'Error al subir el archivo'
      } finally {
        this.isUploading = false
      }
    }
  }
}
</script>

<style scoped>
.file-uploader {
  width: 100%;
}

.drop-zone {
  border-radius: 4px;
  transition: all 0.2s ease;
}

.drop-zone.drag-over {
  background: #f5f5f5;
  border: 2px dashed #1976d2;
}

.uploaded-file {
  padding: 8px 0;
}

.upload-actions {
  padding: 4px 0;
}
</style>
